import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import SearchBar from "@/components/SearchBar";
import { fetchProperties } from "@/lib/api";

// Editorial hero photograph: European heritage castle at evening / blue hour,
// warm light from interior glowing out — Buda Castle, Hungary. Inviting, lit, alive.
// Photographer: Dmitry Romanoff, Unsplash.
const HERO_IMG =
  "https://images.unsplash.com/photo-1709070725088-02e07cd7806a?w=2400&h=1400&fit=crop&crop=entropy&q=85&auto=format";

// Brand spot colours used inline (kept narrowly to the spec'd elements).
const CLARET = "#7A2828";
const WARM_GRAPHITE = "#5A4F3F";
const PANEL_CREAM = "#E5DCC4";

const TIER2_IDS = ["cw-karner", "cw-royal", "cw-fuerstenhof", "cw-schwarzwald"];
const TIER3_IDS = ["cw-ahornhof", "cw-kammweg", "cw-zurpost"];

const LocLabel = ({ children, className = "" }) => (
  <div
    className={`font-ui ${className}`}
    style={{
      color: WARM_GRAPHITE,
      fontSize: "11px",
      letterSpacing: "0.36em",
      fontWeight: 600,
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const ReadHouseLink = ({ size = 14 }) => (
  <span
    className="italic font-display inline-block transition-colors duration-200"
    style={{
      color: CLARET,
      fontSize: `${size}px`,
      textDecorationLine: "underline",
      textDecorationColor: CLARET,
      textDecorationThickness: "1px",
      textUnderlineOffset: "6px",
    }}
  >
    Read the hotel →
  </span>
);

const CastlewoodHome = () => {
  const [props, setProps] = useState([]);
  useEffect(() => {
    fetchProperties({ brand: "castlewood" }).then(setProps);
  }, []);

  const signature = useMemo(
    () => props.find((p) => p.id === "cw-althoernitz"),
    [props]
  );
  const tier2 = useMemo(
    () => TIER2_IDS.map((id) => props.find((p) => p.id === id)).filter(Boolean),
    [props]
  );
  const tier3 = useMemo(
    () => TIER3_IDS.map((id) => props.find((p) => p.id === id)).filter(Boolean),
    [props]
  );

  return (
    <div data-testid="castlewood-home">
      {/* ----------------------------------------------------------------
         HERO — full-bleed editorial photograph, overlay text bottom-left
         ---------------------------------------------------------------- */}
      <section
        className="relative w-full overflow-hidden"
        data-testid="castlewood-hero"
        style={{ height: "min(85vh, 880px)", minHeight: "560px" }}
      >
        <img
          src={HERO_IMG}
          alt="A Castlewood hotel at the blue hour"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.85) brightness(0.92)" }}
        />
        {/* gentle bottom gradient + bottom-left corner darken so overlay text holds */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.05) 38%, rgba(0,0,0,0.55) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at bottom left, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0) 55%)",
          }}
        />

        <div className="absolute inset-0 container-rail flex flex-col justify-end pb-14 md:pb-20">
          <div className="max-w-[860px]">
            <div
              className="font-ui"
              style={{
                color: "#E0BFBF",
                fontSize: "12px",
                letterSpacing: "0.32em",
                fontWeight: 500,
                textTransform: "uppercase",
              }}
              data-testid="hero-eyebrow"
            >
              — Hospitality, kept by hand.
            </div>
            <h1
              className="mt-6 font-display text-white"
              style={{
                fontSize: "clamp(48px, 6vw, 88px)",
                lineHeight: 1.04,
                letterSpacing: "-0.015em",
                fontWeight: 700,
              }}
              data-testid="hero-headline"
            >
              Eight hotels, kept the old way.
            </h1>
            <p
              className="mt-6 italic font-display max-w-[62ch]"
              style={{
                color: "rgba(255,255,255,0.88)",
                fontSize: "clamp(18px, 1.6vw, 24px)",
                lineHeight: 1.55,
                fontWeight: 400,
              }}
            >
              Across Germany, Austria, Switzerland and South Tyrol — one
              operator, three generations of welcome.
            </p>
          </div>
        </div>

        <a
          href="#house-code"
          className="absolute right-4 md:right-10 bottom-6 md:bottom-12 italic font-display transition-colors duration-200"
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: "14px",
            textDecorationLine: "underline",
            textDecorationColor: "rgba(255,255,255,0.4)",
            textUnderlineOffset: "7px",
          }}
          data-testid="hero-house-code-link"
        >
          Read the hotel code →
        </a>
      </section>

      {/* ----------------------------------------------------------------
         SIGNATURE HOTEL — 1 full-width, 16:9, editorial lead
         ---------------------------------------------------------------- */}
      {signature && (
        <section className="bg-background" data-testid="castlewood-signature">
          <div className="container-rail pt-20 md:pt-28">
            <div className="text-center mb-10 md:mb-14">
              <LocLabel>— The Lead Hotel</LocLabel>
            </div>
            <Link
              to={`/castlewood/p/${signature.id}`}
              className="block group"
              data-testid={`signature-${signature.id}`}
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={signature.hero_image}
                  alt={signature.name}
                  className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-95"
                />
              </div>
              <div className="mt-7 md:mt-10 grid md:grid-cols-[1fr_auto] items-end gap-6 md:gap-10">
                <div>
                  <LocLabel>Castlewood · {signature.location}</LocLabel>
                  <div
                    className="mt-4 font-display"
                    style={{
                      fontSize: "clamp(36px, 4.5vw, 48px)",
                      letterSpacing: "-0.015em",
                      lineHeight: 1.06,
                      fontWeight: 700,
                    }}
                  >
                    {signature.name}
                  </div>
                  <div className="mt-3 h-px w-14" style={{ background: CLARET }} />
                  <p
                    className="mt-5 italic font-display max-w-[58ch]"
                    style={{
                      fontSize: "clamp(18px, 1.4vw, 22px)",
                      lineHeight: 1.45,
                    }}
                  >
                    {signature.tagline}
                  </p>
                </div>
                <ReadHouseLink />
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------
         TIER 2 — 4 hotels, 2×2, portrait 4:5
         ---------------------------------------------------------------- */}
      {tier2.length > 0 && (
        <section className="bg-background" data-testid="castlewood-tier2">
          <div className="container-rail pt-16 md:pt-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-16 md:gap-y-20">
              {tier2.map((p) => (
                <Link
                  key={p.id}
                  to={`/castlewood/p/${p.id}`}
                  className="block group"
                  data-testid={`tier2-${p.id}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={p.hero_image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-95"
                    />
                  </div>
                  <div className="mt-6">
                    <LocLabel>{p.location}</LocLabel>
                    <div
                      className="mt-3 font-display"
                      style={{ fontSize: "24px", lineHeight: 1.15, fontWeight: 700 }}
                    >
                      {p.name}
                    </div>
                    <div
                      className="mt-3 h-px w-10"
                      style={{ background: CLARET }}
                    />
                    <p
                      className="mt-4 italic font-display max-w-[42ch]"
                      style={{ fontSize: "16px", lineHeight: 1.5 }}
                    >
                      {p.tagline}
                    </p>
                    <div className="mt-5">
                      <ReadHouseLink />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------
         TIER 3 — 3 hotels, tight row, square
         ---------------------------------------------------------------- */}
      {tier3.length > 0 && (
        <section className="bg-background" data-testid="castlewood-tier3">
          <div className="container-rail pt-16 md:pt-24">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10">
              {tier3.map((p) => (
                <Link
                  key={p.id}
                  to={`/castlewood/p/${p.id}`}
                  className="block group"
                  data-testid={`tier3-${p.id}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={p.hero_image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-95"
                    />
                  </div>
                  <div className="mt-4">
                    <LocLabel>{p.location}</LocLabel>
                    <div
                      className="mt-2 font-display"
                      style={{ fontSize: "20px", lineHeight: 1.2, fontWeight: 700 }}
                    >
                      {p.name}
                    </div>
                    <div
                      className="mt-2 h-px w-8"
                      style={{ background: CLARET }}
                    />
                    <p
                      className="mt-3 italic font-display max-w-[34ch]"
                      style={{ fontSize: "14px", lineHeight: 1.5 }}
                    >
                      {p.tagline}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-14 md:mt-20 text-center">
              <Link
                to="/castlewood/search"
                className="italic font-display transition-colors duration-200"
                style={{
                  color: CLARET,
                  fontSize: "15px",
                  textDecorationLine: "underline",
                  textDecorationColor: CLARET,
                  textDecorationThickness: "1px",
                  textUnderlineOffset: "8px",
                }}
                data-testid="read-each-house"
              >
                Read each hotel in full →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ----------------------------------------------------------------
         PULL-QUOTE — italic serif, 120px claret rails, illuminated A
         ---------------------------------------------------------------- */}
      <section className="bg-background" data-testid="castlewood-pullquote">
        <div className="py-[96px] md:py-[160px]">
          <div className="max-w-[880px] mx-auto px-6 text-center">
            {/* Top hairline */}
            <div
              className="mx-auto"
              style={{ width: "120px", height: "1px", background: CLARET }}
              aria-hidden="true"
            />

            {/* The quote */}
            <blockquote
              className="italic font-display"
              style={{
                marginTop: "48px",
                marginBottom: "48px",
                fontSize: "clamp(36px, 5vw, 64px)",
                lineHeight: 1.25,
                fontWeight: 400,
                color: "#1F1A14",
                fontStyle: "italic",
              }}
            >
              <span
                className="font-display"
                style={{
                  color: CLARET,
                  fontSize: "2.4em",
                  lineHeight: "0.85",
                  fontStyle: "italic",
                  fontWeight: 400,
                  verticalAlign: "baseline",
                  marginRight: "0.04em",
                  display: "inline-block",
                }}
              >
                E
              </span>
              {"ight hotels. Three generations. One idea of what hospitality is."}
            </blockquote>

            {/* Bottom hairline */}
            <div
              className="mx-auto"
              style={{ width: "120px", height: "1px", background: CLARET }}
              aria-hidden="true"
            />

            {/* Attribution */}
            <div
              className="font-ui"
              style={{
                marginTop: "24px",
                color: WARM_GRAPHITE,
                fontSize: "11px",
                letterSpacing: "0.36em",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              — The Castlewood Code, Section One
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------------
         THREE THINGS, KEPT FAITHFULLY — UNCHANGED
         ---------------------------------------------------------------- */}
      <section
        id="house-code"
        className="bg-secondary"
        data-testid="castlewood-pillars"
      >
        <div className="container-rail py-16 md:py-24">
          <div className="text-center">
            <BrandLabel withRule>The Hotel Code</BrandLabel>
            <BrandHeading level={2} className="mt-4 max-w-[26ch] mx-auto">
              Three things, kept faithfully.
            </BrandHeading>
            <p className="mt-4 max-w-[60ch] mx-auto text-muted-foreground font-body leading-relaxed">
              Three commitments that keep a hotel from becoming a chain. The rest
              we let the hotel decide.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-10">
            {[
              {
                label: "Tradition — Faithful",
                title: "Tradition",
                body: "Restoration without dilution. Each beam, each tile, each painted ceiling stays as it was.",
              },
              {
                label: "Family — Personal",
                title: "Family",
                body: "A hotel is not a chain. The hosts are the same year on year; the guest book remembers each face.",
              },
              {
                label: "Nature — Connected",
                title: "Nature connected",
                body: "From the Black Forest to the Brenner, each hotel is bound by walking distance to a piece of the landscape that holds it.",
              },
            ].map((pillar) => (
              <div key={pillar.title} className="text-center">
                <BrandLabel>{pillar.label}</BrandLabel>
                <div className="mt-4 font-display text-[26px] font-[700]">
                  {pillar.title}
                </div>
                <p className="mt-3 text-[14px] text-muted-foreground font-body leading-relaxed max-w-[36ch] mx-auto">
                  {pillar.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------------
         PLAN A STAY — search bar relocated here, panel-cream backdrop
         ---------------------------------------------------------------- */}
      <section data-testid="castlewood-plan" style={{ background: PANEL_CREAM }}>
        <div className="container-rail py-20 md:py-28">
          <div className="text-center mb-10 md:mb-14">
            <div
              className="font-ui"
              style={{
                color: CLARET,
                fontSize: "12px",
                letterSpacing: "0.36em",
                fontWeight: 500,
                textTransform: "uppercase",
              }}
              data-testid="plan-eyebrow"
            >
              — Plan a stay
            </div>
            <BrandHeading level={2} className="mt-5">
              Where, and when.
            </BrandHeading>
            <p className="mt-4 max-w-[52ch] mx-auto font-body italic text-foreground/80">
              Choose a hotel. Choose the days. We will keep them for you.
            </p>
          </div>
          <div className="max-w-[1080px] mx-auto">
            <SearchBar scope="castlewood" defaultBrand="castlewood" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default CastlewoodHome;
